import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
export const COUNTRIES = [
    // Mais usados
    { code: 'BR', name: 'Brasil' },
    { code: 'US', name: 'Estados Unidos' },
    { code: 'PT', name: 'Portugal' },
    // Américas
    { code: 'AR', name: 'Argentina' },
    { code: 'BO', name: 'Bolívia' },
    { code: 'CA', name: 'Canadá' },
    { code: 'CL', name: 'Chile' },
    { code: 'CO', name: 'Colômbia' },
    { code: 'CR', name: 'Costa Rica' },
    { code: 'DO', name: 'República Dominicana' },
    { code: 'EC', name: 'Equador' },
    { code: 'SV', name: 'El Salvador' },
    { code: 'GT', name: 'Guatemala' },
    { code: 'HN', name: 'Honduras' },
    { code: 'MX', name: 'México' },
    { code: 'NI', name: 'Nicarágua' },
    { code: 'PA', name: 'Panamá' },
    { code: 'PY', name: 'Paraguai' },
    { code: 'PE', name: 'Peru' },
    { code: 'PR', name: 'Porto Rico' },
    { code: 'UY', name: 'Uruguai' },
    { code: 'VE', name: 'Venezuela' },
    // Europa
    { code: 'DE', name: 'Alemanha' },
    { code: 'AT', name: 'Áustria' },
    { code: 'BE', name: 'Bélgica' },
    { code: 'BG', name: 'Bulgária' },
    { code: 'HR', name: 'Croácia' },
    { code: 'DK', name: 'Dinamarca' },
    { code: 'SK', name: 'Eslováquia' },
    { code: 'SI', name: 'Eslovênia' },
    { code: 'ES', name: 'Espanha' },
    { code: 'EE', name: 'Estônia' },
    { code: 'FI', name: 'Finlândia' },
    { code: 'FR', name: 'França' },
    { code: 'GR', name: 'Grécia' },
    { code: 'HU', name: 'Hungria' },
    { code: 'IE', name: 'Irlanda' },
    { code: 'IT', name: 'Itália' },
    { code: 'LV', name: 'Letônia' },
    { code: 'LT', name: 'Lituânia' },
    { code: 'LU', name: 'Luxemburgo' },
    { code: 'MT', name: 'Malta' },
    { code: 'NO', name: 'Noruega' },
    { code: 'NL', name: 'Países Baixos' },
    { code: 'PL', name: 'Polônia' },
    { code: 'GB', name: 'Reino Unido' },
    { code: 'CZ', name: 'República Tcheca' },
    { code: 'RO', name: 'Romênia' },
    { code: 'SE', name: 'Suécia' },
    { code: 'CH', name: 'Suíça' },
    { code: 'UA', name: 'Ucrânia' },
    // África
    { code: 'ZA', name: 'África do Sul' },
    { code: 'AO', name: 'Angola' },
    { code: 'CV', name: 'Cabo Verde' },
    { code: 'EG', name: 'Egito' },
    { code: 'GW', name: 'Guiné-Bissau' },
    { code: 'KE', name: 'Quênia' },
    { code: 'MA', name: 'Marrocos' },
    { code: 'MZ', name: 'Moçambique' },
    { code: 'NG', name: 'Nigéria' },
    { code: 'ST', name: 'São Tomé e Príncipe' },
    // Ásia, Oceania e Oriente Médio
    { code: 'SA', name: 'Arábia Saudita' },
    { code: 'AU', name: 'Austrália' },
    { code: 'CN', name: 'China' },
    { code: 'KR', name: 'Coreia do Sul' },
    { code: 'AE', name: 'Emirados Árabes Unidos' },
    { code: 'PH', name: 'Filipinas' },
    { code: 'HK', name: 'Hong Kong' },
    { code: 'IN', name: 'Índia' },
    { code: 'ID', name: 'Indonésia' },
    { code: 'IL', name: 'Israel' },
    { code: 'JP', name: 'Japão' },
    { code: 'MY', name: 'Malásia' },
    { code: 'NZ', name: 'Nova Zelândia' },
    { code: 'SG', name: 'Singapura' },
    { code: 'TH', name: 'Tailândia' },
    { code: 'TW', name: 'Taiwan' },
    { code: 'TR', name: 'Turquia' },
    { code: 'VN', name: 'Vietnã' },
];
const PINNED = ['BR', 'US', 'PT'];
/** Bandeira em emoji a partir do código ISO (sem imagens externas). */
export function flagEmoji(code) {
    return code
        .toUpperCase()
        .split('')
        .map((c) => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65))
        .join('');
}
/** Remove acentos para a busca aceitar "Mexico" e "México". */
function normalize(text) {
    return text
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase();
}
export const CountrySelect = React.forwardRef(function CountrySelect({ id, value, onChange, className, invalid, disabled }, ref) {
    const [open, setOpen] = React.useState(false);
    const selected = COUNTRIES.find((c) => c.name === value) || null;
    const pinned = COUNTRIES.filter((c) => PINNED.includes(c.code));
    const others = COUNTRIES.filter((c) => !PINNED.includes(c.code)).sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
    const pick = (country) => {
        onChange(country.name);
        setOpen(false);
    };
    const item = (c) => (_jsxs(CommandItem, { value: `${normalize(c.name)} ${c.code.toLowerCase()}`, onSelect: () => pick(c), children: [_jsx("span", { className: "w-6 text-base leading-none", "aria-hidden": "true", children: flagEmoji(c.code) }), _jsx("span", { className: "flex-1 truncate", children: c.name }), _jsx(Check, { className: cn('h-4 w-4', (selected === null || selected === void 0 ? void 0 : selected.code) === c.code ? 'opacity-100' : 'opacity-0') })] }, c.code));
    return (_jsxs(Popover, { open: open, onOpenChange: (v) => !disabled && setOpen(v), children: [_jsx(PopoverTrigger, { asChild: true, children: _jsxs(Button, { id: id, ref: ref, type: "button", variant: "outline", role: "combobox", "aria-expanded": open, disabled: disabled, className: cn('h-10 w-full justify-between px-3 font-normal shadow-sm', !selected && 'text-muted-foreground', invalid && 'border-destructive', className), children: [_jsxs("span", { className: "flex min-w-0 items-center gap-2", children: [selected ? _jsx("span", { className: "text-base leading-none", "aria-hidden": "true", children: flagEmoji(selected.code) }) : null, _jsx("span", { className: "truncate", children: selected ? selected.name : 'Selecione o país' })] }), _jsx(ChevronsUpDown, { className: "h-4 w-4 shrink-0 opacity-50" })] }) }), _jsx(PopoverContent, { className: "w-[--radix-popover-trigger-width] min-w-[260px] p-0", align: "start", children: _jsxs(Command, { filter: (itemValue, search) => (itemValue.includes(normalize(search)) ? 1 : 0), children: [_jsx(CommandInput, { placeholder: "Buscar pa\u00EDs\u2026" }), _jsxs(CommandList, { className: "max-h-64", children: [_jsx(CommandEmpty, { children: "Nenhum pa\u00EDs encontrado." }), _jsx(CommandGroup, { heading: "Mais usados", children: pinned.map(item) }), _jsx(CommandGroup, { heading: "Todos os pa\u00EDses", children: others.map(item) })] })] }) })] }));
});
